import { definedMap } from "@glideapps/ts-necessities";

import {
    TableGlideType,
    ComponentKind,
    ComponentDescription,
    getTablesWithPrimitiveColumns,
    getPrimitiveColumns,
    TypeSchema,
    InputOutputTables,
    TableColumn,
    MutatingScreenKind,
    getNonHiddenColumns,
} from "../description";
import { SerializedComponent } from "../../components/SerializedApp";
import { ComponentBuilder, ComponentDescriptor, ComponentConfiguratorContext, SchemaInspector } from ".";
import { ComponentHandlerBase } from "./handler";
import { SerializedElementBuilder } from "../serialized-component";
import { makePrimaryKeyPropertyHandler, getPrimaryKeyProperty } from "./descriptor-utils";
import {
    ColumnPropertyHandler,
    TablePropertyHandler,
    ColumnPropertyFlag,
    isRequiredPropertyHandler,
    StringPropertyHandler,
} from "./property-handlers";
import { PropertySection } from "./property-sections";
import { makeValidationFlagForProperty, setColumnInRowForEditComponent } from "../builder-utils";
import { getDocURL } from "../../docUrl";
import { undefinedIfEmptyString } from "../../Support";

const ComponentKindChoice: ComponentKind = "choice";

function getAllowedChoiceColumns(choiceTable: TableGlideType): ReadonlyArray<TableColumn> {
    const columns = getPrimitiveColumns(choiceTable);
    return getNonHiddenColumns(columns);
}

function getAllowedChoiceTables(_desc: ComponentDescription, schema: TypeSchema): ReadonlyArray<TableGlideType> {
    return getTablesWithPrimitiveColumns(schema.tables).filter(
        choiceTable => getAllowedChoiceColumns(choiceTable).length > 0
    );
}

const valuePropertyHandler: ColumnPropertyHandler<ComponentDescription> = new ColumnPropertyHandler(
    "propertyName",
    "Column",
    [
        ColumnPropertyFlag.Required,
        ColumnPropertyFlag.Editable,
        ColumnPropertyFlag.DefaultCaption,
        ColumnPropertyFlag.EditedInApp,
    ],
    undefined,
    undefined,
    (propertyTable, desc, schema) => {
        const columns = getPrimitiveColumns(propertyTable);
        const choiceTable = tablePropertyHandler.lookupTable(desc, schema);
        if (propertyTable !== choiceTable) {
            return columns;
        }
        const choicesColumnName = columnPropertyHandler.getColumnName(desc);
        return columns.filter(c => c.name !== choicesColumnName);
    },
    "string",
    PropertySection.Data
);
const tablePropertyHandler = new TablePropertyHandler<ComponentDescription>(
    "choicesTableName",
    "Sheet",
    true,
    getAllowedChoiceTables,
    PropertySection.Data
);
const columnPropertyHandler = new ColumnPropertyHandler<ComponentDescription>(
    "choicesColumnName",
    "Values",
    [ColumnPropertyFlag.Required, ColumnPropertyFlag.Editable],
    (_componentTables: InputOutputTables, desc: ComponentDescription, schema: SchemaInspector) =>
        tablePropertyHandler.lookupTable(desc, schema),
    ["choices"],
    choiceTable => getAllowedChoiceColumns(choiceTable),
    "string",
    PropertySection.Data
);

const titlePropertyHandler = new StringPropertyHandler<ComponentDescription>(
    "title",
    "Title",
    "the default title",
    false,
    "Choice",
    PropertySection.Design
);

export class ChoiceComponentHandler extends ComponentHandlerBase<ComponentDescription> {
    constructor() {
        super(ComponentKindChoice);
    }

    public getIsEditor(): boolean {
        return true;
    }

    public needValidation(desc: ComponentDescription): boolean {
        return isRequiredPropertyHandler.getSwitch(desc);
    }

    public entryForTableColumn(
        builder: ComponentBuilder,
        desc: ComponentDescription,
        tables: InputOutputTables,
        mutatingScreenKind: MutatingScreenKind | undefined,
        _validFlag: SerializedComponent | undefined,
        outputContext: SerializedComponent
    ): SerializedComponent {
        const { b } = builder;
        const primaryKeyProperty = getPrimaryKeyProperty(desc);

        const propertyName = valuePropertyHandler.getColumnName(desc);
        const choicesTableName = tablePropertyHandler.getTableName(desc);
        const choicesColumnName = columnPropertyHandler.getColumnName(desc);
        const title = titlePropertyHandler.getString(desc);
        if (propertyName === undefined || choicesTableName === undefined || choicesColumnName === undefined)
            return b.errorUI;

        const choices = b.filter(
            b.map(
                builder.fetchTableRows(choicesTableName, false),
                b.getProperty(choicesColumnName, b.getContext, tables.input)
            ),
            b.isDefined(b.getContext)
        );

        const setColumnsResult = setColumnInRowForEditComponent(
            b,
            mutatingScreenKind,
            propertyName,
            b.getProperty(propertyName, outputContext, tables.output),
            undefined,
            tables.output,
            primaryKeyProperty,
            b.getContext
        );
        if (setColumnsResult === undefined) return b.emptyUI;
        const { lets, valueToModify, action, isEnabled } = setColumnsResult;

        const isRequired = isRequiredPropertyHandler.getSwitch(desc);
        return b.let(
            lets,
            b.ui("Choice", {
                value: valueToModify,
                choices,
                caption: definedMap(title, x => b.constant(x)),
                onChange: action,
                isEnabled,
                isRequired: b.constant(isRequired),
            })
        );
    }

    public makeValidationFlag(
        b: SerializedElementBuilder,
        desc: ComponentDescription,
        outputContext: SerializedComponent,
        table: TableGlideType
    ): SerializedComponent | undefined {
        return makeValidationFlagForProperty(b, outputContext, valuePropertyHandler.getColumnName(desc), table);
    }

    public getDescriptor(
        _desc: ComponentDescription,
        table: TableGlideType | undefined,
        ccc: ComponentConfiguratorContext
    ): ComponentDescriptor<ComponentDescription> {
        return {
            name: "Choice",
            description: "Choose one out of many options",
            img: "componentChoice",
            group: "Pickers",
            helpUrl: getDocURL("choice"),
            properties: [
                valuePropertyHandler,
                titlePropertyHandler,
                tablePropertyHandler,
                columnPropertyHandler,
                ...makePrimaryKeyPropertyHandler(ccc, table),
                isRequiredPropertyHandler,
                ...this.getBasePropertyDescriptors(),
            ],
            isLegacy: true,
        };
    }

    public getDescriptiveName(
        desc: ComponentDescription,
        tables: InputOutputTables,
        ccc: ComponentConfiguratorContext
    ): [string, string] {
        const names = super.getDescriptiveName(desc, tables, ccc);
        const caption = titlePropertyHandler.getString(desc);
        return [names[0], undefinedIfEmptyString(caption) ?? names[1]];
    }
}
